import {Component, effect, EffectRef, inject, untracked} from '@angular/core';
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogCalendarEventComponent} from "../dialog-calendar-event/dialog-calendar-event.component";
import {ItemCalendarEventComponent} from "../item-calendar-event/item-calendar-event.component";
import {DialogCalendarEventDetailsComponent} from "../dialog-calendar-event-details/dialog-calendar-event-details.component";
import {RouterService} from "../../../shared/services/router.service";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {isSameDay} from '../mapper/calendar-helper.mapper';
import {CalendarStore} from '../stores/calendar-store';
import {CalendarEventPriority} from '../../../shared/types/generated/calendarEventPriority';
import {CalendarEventResource} from '../../../shared/types/generated/calendarEventResource';
import {View} from '@syncfusion/ej2-angular-schedule';
import {ProfileService} from '../../../shared/services/profile.service';
import {CapabilitiesService} from '../../../shared/services/capabilities.service';
import {WidgetCalendarEvent} from '../../../shared/types/calendar-types/widgetCalendarEvent.interface';

@Component({
    selector: 'app-calendar-widget',
    imports: [
        IconBadgeComponent,
        LinkComponent,
        TranslatePipe,
        DialogCalendarEventComponent,
        ItemCalendarEventComponent,
        DialogCalendarEventDetailsComponent,
        ContentComponent
    ],
    templateUrl: './widget-calendar.component.html',
    styleUrl: './widget-calendar.component.scss'
})
export class WidgetCalendarComponent {
  public routerService: RouterService = inject(RouterService);
  public calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);
  private profileService: ProfileService = inject(ProfileService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);

  public calendarEventDialog: boolean = false;
  public calendarEventDetailsDialog: boolean = false;
  public selectedEvent: CalendarEventResource | null = null;

  private translateEffect: EffectRef = effect(() => {
    const isTrans = !!(this.profileService.profile()?.user.auto_translation ?? false);
    untracked(() => {
      this.calendarStore.setTranslate(isTrans);
    });
  });
  private initWidgetStoreEffect: EffectRef = effect(() => {
    const capabilitiesLoaded = this.capabilitiesService.capabilitiesLoaded();

    if (!capabilitiesLoaded) {
      this.capabilitiesService.ensureUserCapabilitiesLoaded();
      return;
    }

    untracked(() => {
      this.calendarStore.initWidgetScheduler();
    });
  });

  public getDotClass(priority: CalendarEventPriority | null): string {
    switch (priority) {
      case 'high': return 'bg-danger';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  public isToday(date: Date): boolean {
    const today = new Date();
    return isSameDay(today, date);
  }

  public onDayClicked(day: WidgetCalendarEvent): void {
    if (!day.event || day.event.high === 0 && day.event.medium === 0 && day.event.low === 0) {
      return;
    }

    this.calendarStore.setSelectedDate(day.date);
    const savedView = this.calendarStore.getViewFromCookie();
    this.calendarStore.setView((savedView as View) || this.calendarStore.schedulerOptions.view());
    this.routerService.navigateTo('/calendar');
  }

  public onWeekViewClicked(): void {
    const startOfWeek = this.calendarStore.widgetWeekStartDate();
    this.calendarStore.setSelectedDate(startOfWeek);
    this.calendarStore.setView('Week');

    this.routerService.navigateTo('/calendar');
  }

  public openNewEventDialog(): void {
    this.selectedEvent = null;
    this.calendarEventDialog = true;
  }

  public openEditEventDialog(event: CalendarEventResource): void {
    const resolvedEvent = this.calendarStore.resolveEventWithSourceCalendar(event.id, event.calendar_id);
    this.selectedEvent = resolvedEvent?.event ?? event;
    this.calendarEventDialog = true;
  }

  public openEventDetailsDialog(event: CalendarEventResource): void {
    if (this.isMaskedEvent(event)) {
      return;
    }

    const resolvedEvent = this.calendarStore.resolveEventWithSourceCalendar(event.id, event.calendar_id);
    this.selectedEvent = resolvedEvent?.event ?? event;
    this.calendarEventDetailsDialog = true;
  }

  public isMaskedEvent(event: Pick<CalendarEventResource, 'name' | 'name_translated'> | null | undefined): boolean {
    return event?.name === null && event?.name_translated === null;
  }
}
